/**
 * Precision Ratify — board self-service token admin.
 *
 * Compliance officers issue, list, copy, and revoke the long-lived links
 * board members use to see their own approval history. The raw token is
 * shown ONCE at issuance — after that, only the metadata.
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

function fmtDate(d) { return d ? new Date(d).toLocaleDateString() : '—'; }

function portalUrl(rawToken) {
  // The frontend route /board/:token is served by this same React app.
  if (typeof window === 'undefined') return `/board/${rawToken}`;
  return `${window.location.origin}/board/${rawToken}`;
}

export default function BoardTokensAdmin({ onBack }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [showIssue, setShowIssue] = useState(false);
  const [issueEmail, setIssueEmail] = useState('');
  const [issueName, setIssueName] = useState('');
  const [issuedLink, setIssuedLink] = useState(null); // { url, expires_at } shown once

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/approvals/board-tokens`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) setTokens(d.tokens || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    if (!issueEmail.trim()) { setError('Email is required.'); return; }
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/approvals/board-tokens`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: issueEmail.trim(),
          display_name: issueName.trim() || null,
        }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Failed to issue link');
      setIssuedLink({ url: portalUrl(d.raw_token), expires_at: d.expires_at });
      setIssueEmail(''); setIssueName('');
      await load();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this board link? The board member will no longer be able to use it.')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/approvals/board-tokens/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Failed to revoke');
      await load();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-700 mb-4 inline-flex items-center gap-1">
          ← Back to SignoffHub
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-[11px] uppercase font-bold tracking-wider text-indigo-600">Board self-service links</div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mt-0.5">Board portal access</h2>
            <p className="text-slate-500 text-sm mt-1 max-w-2xl">
              Issue a long-lived link a board member can bookmark to see every package they've been on. The link is hashed at rest, revocable at any time, and personal to one email.
            </p>
          </div>
          <button
            onClick={() => { setShowIssue(true); setIssuedLink(null); setError(''); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
            Issue a board link
          </button>
        </div>

        {error && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">{error}</div>
        )}

        {issuedLink && (
          <div className="mb-5 bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
            <div className="text-xs uppercase font-bold tracking-wider text-emerald-700 mb-1">Link issued — copy now</div>
            <div className="text-xs text-slate-600 mb-2">
              For security, this raw link is shown <strong>only once</strong>. Send it to the board member; we won't show it again.
            </div>
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={issuedLink.url}
                className="flex-1 text-xs font-mono bg-white border border-emerald-200 rounded-lg px-3 py-2"
              />
              <button
                onClick={() => copy(issuedLink.url)}
                className="px-3 py-2 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              >
                Copy
              </button>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Expires {fmtDate(issuedLink.expires_at)}.</div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-slate-400 text-sm">Loading…</div>
          ) : tokens.length === 0 ? (
            <div className="py-12 text-center">
              <div className="text-4xl mb-2">🔗</div>
              <div className="text-slate-800 font-semibold">No board links yet</div>
              <div className="text-slate-400 text-sm mt-1">Issue one to give a board member ongoing access to their own approvals.</div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {tokens.map((t) => {
                const revoked = !!t.revoked_at;
                const expired = !revoked && t.expires_at && new Date(t.expires_at) < new Date();
                const status = revoked ? 'revoked' : expired ? 'expired' : 'active';
                return (
                  <li key={t.id} className="flex items-center justify-between gap-4 px-5 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 truncate">{t.display_name || t.email}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${
                          status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : status === 'expired' ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-slate-100 text-slate-500 border-slate-200'
                        }`}>{status}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {t.email} · issued {fmtDate(t.issued_at)} · expires {fmtDate(t.expires_at)}
                        {t.last_seen_at && <> · last seen {fmtDate(t.last_seen_at)}</>}
                      </div>
                    </div>
                    {status === 'active' && (
                      <button
                        onClick={() => revoke(t.id)}
                        disabled={busy}
                        className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {showIssue && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <h3 className="font-bold text-slate-900 text-lg">Issue a board link</h3>
              <p className="text-sm text-slate-500 mt-1">
                The board member will get a personal URL they can bookmark. They can't sign decisions from it — that still requires the per-package emailed invitation. This link is only for them to see their own history.
              </p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Email *</label>
                  <input
                    value={issueEmail}
                    onChange={(e) => setIssueEmail(e.target.value)}
                    placeholder="director@board.example.com"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Display name (optional)</label>
                  <input
                    value={issueName}
                    onChange={(e) => setIssueName(e.target.value)}
                    placeholder="Aisha Khan"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setShowIssue(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</button>
                <button
                  onClick={async () => { await issue(); setShowIssue(false); }}
                  disabled={busy || !issueEmail.trim()}
                  className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50"
                >
                  {busy ? 'Issuing…' : 'Issue link'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
